import {
  RequestForQuoteStep,
  useRfqDetailsQuery,
  useShippingLocationGroupsQuery,
} from '@/buyers/_gen/gql'
import RFQCommentForm from '@/buyers/components/RFQCommentForm'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Address from '@/gf/components/Address'
import Card from '@/gf/components/Card'
import Desc from '@/gf/components/Desc'
import Layout from '@/gf/components/Layout'
import Link from '@/gf/components/Link'
import LinkButton from '@/gf/components/LinkButtonOld'
import RfqEvents from '@/gf/components/RfqEvents'
import { useTimelineEventsForRfq } from '@/gf/hooks/useTimelineEvents'
import useToggle from '@/gf/hooks/useToggle'
import Phone from '@/gf/modules/Phone'
import StoreOrderEventContext from '@/gf/modules/StoreOrderEvent'
import Time from '@/gf/modules/Time'
import UserContext from '@/gf/modules/User'
import nth from 'lodash/nth'
import { Link as RouterLink, Route, Routes, useNavigate } from 'react-router-dom'
import { useContext } from '../RFQ'
import Notifications from './Details/Notifications'
import EditWorkOrderModal from './EditWorkOrderModal'
import EditRequestLocationGroupModal from './EditRequestLocationGroupModal'
import EditRequestBillingCompanyModal from './EditRequestBillingCompanyModal'
import WorkOrderJobs from './WorkOrderJobs'
import BillingAddress from '@/gf/components/BillingAddress'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'

const Details = ({ refetch }: { refetch: () => Promise<unknown> }) => {
  const {
    user,
    organization: { id: orgId },
  } = useSession()

  const { rfq } = useContext()
  const navigate = useNavigate()
  const eventsSearch = useTimelineEventsForRfq(rfq.id).data?.eventsSearch
  const [workOrderOpen, workOrderToggle] = useToggle()
  const [locationGroupOpen, locationGroupToggle] = useToggle()
  const [billingCompanyOpen, billingCompanyToggle] = useToggle()
  const gqlClient = useGqlClient()

  const { data: locationGroupsData } = useShippingLocationGroupsQuery({
    client: gqlClient,
    variables: { orgId, filter: null },
    skip: !orgId,
  })

  const orgHasLocationGroups = (locationGroupsData?.org?.shippingLocationGroups ?? []).length > 0

  const {
    data: rfqNext,
    org: detailsOrg,
    refetchRequest,
  } = (() => {
    const { refetch: refetchNext, data } = useRfqDetailsQuery({
      variables: { orgId, filter: JSON.stringify(['id_eq', rfq.id]) },
      client: gqlClient,
    })

    const request = data && nth(data.rfqs, 0)
    return { data: request, org: data?.org, refetchRequest: refetchNext }
  })()

  if (!rfqNext) return null

  const shippingAddress = rfq.shippingAddress ?? rfq.user?.shippingAddress
  const billingAddress = rfqNext.billingAddress
  const orgMachine = rfqNext.orgMachines && rfqNext.orgMachines[0]

  // Only orgs that bill by business unit have a billing company to show. Mirrors the
  // flag that decides whether the create/approve screens render the picker at all.
  const billingCompany = rfqNext.billingCompany
  const showBillingCompany = detailsOrg?.requireBillingCompany || !!billingCompany

  // Past shipping, changing it would re-label Gearflow's paperwork while a finalized
  // Stripe net-terms invoice keeps billing the old company - so the edit stops here.
  // Same line the mutation enforces (GEA-3719).
  const billingCompanyEditable = [
    RequestForQuoteStep.Inbound,
    RequestForQuoteStep.Submitted,
    RequestForQuoteStep.Quoted,
    RequestForQuoteStep.PoSent,
  ].includes(rfqNext.step)

  // The jobs block is gated by data, not a flag: you need an M5 org and a work
  // order for it to mean anything. In practice that is Primoris.
  const workOrderNumber = detailsOrg?.assetworks ? rfqNext.workOrderNumber : null

  const hasIncompleteShippingAddress =
    shippingAddress &&
    (!shippingAddress.lineOne || !shippingAddress.city || !shippingAddress.postalCode)

  return (
    <Layout>
      <EditWorkOrderModal
        refetch={refetchRequest}
        onClose={workOrderToggle.off}
        open={workOrderOpen}
        requestId={rfq.id}
        woNumber={rfqNext.workOrderNumber}
        unitNumber={nth(rfq.orgMachines, 0)?.name}
        orgMachine={orgMachine}
        fleetioWorkOrderId={rfqNext.fleetioWorkOrderId}
        externalWorkOrderId={rfqNext.externalWorkOrderId ?? null}
      />

      <EditRequestLocationGroupModal
        refetch={refetch}
        onClose={locationGroupToggle.off}
        open={locationGroupOpen}
        requestId={rfq.id}
        currentLocationGroupId={rfq.shippingLocationGroup?.id}
      />

      <EditRequestBillingCompanyModal
        refetch={refetchRequest}
        onClose={billingCompanyToggle.off}
        open={billingCompanyOpen}
        requestId={rfq.id}
        currentBillingCompanyId={billingCompany?.id}
      />

      <Routes>
        <Route path="notifications" element={<Notifications />} />
      </Routes>

      <Layout.Section type="ipad" className="xl:col-span-4">
        <Card title="Timeline">
          <Card.Section>
            <Card.SubSection>
              <RFQCommentForm
                rfqId={rfq.id}
                refetch={refetch}
                isAdmin={UserContext.isAdmin(user)}
                openMessagesTab={() => navigate('messages')}
              />
            </Card.SubSection>
            <Card.SubSection>
              <RfqEvents
                events={eventsSearch?.rfqEvents}
                storeOrderEvents={eventsSearch?.storeOrderEvents}
                storeOrderEventMapper={StoreOrderEventContext.getBuyerEventDisplayData}
                showStoreNames
              />
            </Card.SubSection>
          </Card.Section>
        </Card>
      </Layout.Section>
      <Layout.Section type="ipad" className="xl:col-span-2">
        {rfq.user && (
          <Card title="Info">
            <Card.Section>
              <Desc.List>
                <Desc.Row title="Created by">{rfq.user.displayName}</Desc.Row>
                <Desc.Row title="Created on">{Time.formatDateTime(rfq.insertedAt)}</Desc.Row>
                <Desc.Row title="Contact">
                  <div>
                    <p>{rfq.fullName}</p>
                    {rfq.emailAddress && <p className="break-words">{rfq.emailAddress}</p>}
                    <p>{Phone.format(rfq.phoneNumber)}</p>
                  </div>
                </Desc.Row>
                <Desc.Row title="Shipping address">
                  {shippingAddress ? (
                    <div className="space-y-1">
                      {rfq.shippingLocation && (
                        <p className="font-medium leading-tight">{rfq.shippingLocation.name}</p>
                      )}
                      <Address address={shippingAddress} />
                      {hasIncompleteShippingAddress && rfq.shippingLocation && (
                        <RouterLink
                          to={`/locations/${rfq.shippingLocation.id}?edit=true`}
                          className="inline-flex items-center px-2 py-1 rounded-md bg-yellow-50 border border-yellow-300 text-yellow-800 text-xs font-medium hover:bg-yellow-100 transition-colors"
                        >
                          <ExclamationTriangleIcon className="w-3 h-3 mr-1 text-yellow-600" />
                          Incomplete address - click to update
                        </RouterLink>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-400 italic">(none)</p>
                  )}
                </Desc.Row>
                <Desc.Row title="Billing address">
                  {billingAddress ? (
                    <BillingAddress companyName={rfqNext.company.name} address={billingAddress} />
                  ) : (
                    <p className="text-gray-400 italic">(none)</p>
                  )}
                </Desc.Row>
                {showBillingCompany && (
                  <Desc.Row title="Billing company">
                    {billingCompany ? (
                      [billingCompany.code, billingCompany.name].filter(Boolean).join(' ')
                    ) : (
                      <span className="text-gray-400 italic">(none)</span>
                    )}{' '}
                    {billingCompanyEditable && (
                      <LinkButton onClick={billingCompanyToggle.on}>Edit</LinkButton>
                    )}
                  </Desc.Row>
                )}
                <Desc.Row title="Work order number">
                  {rfqNext.workOrderNumber}{' '}
                  <LinkButton onClick={workOrderToggle.on}>Edit</LinkButton>
                </Desc.Row>
                {orgHasLocationGroups && (
                  <Desc.Row title="Location group">
                    {rfq.shippingLocationGroup?.name || (
                      <span className="text-gray-400 italic">(none)</span>
                    )}{' '}
                    <LinkButton onClick={locationGroupToggle.on}>Edit</LinkButton>
                  </Desc.Row>
                )}
              </Desc.List>
            </Card.Section>
          </Card>
        )}

        {workOrderNumber && <WorkOrderJobs workOrderNumber={workOrderNumber} />}

        <Card title="Notifications" headerButton={<Link.T to="notifications">Edit</Link.T>}>
          <Card.Section>
            {rfq.buyerNotificationUsers.length === 0 ? (
              <p className="text-sm text-gray-700">
                None. Click &apos;Edit&apos; to subscribe/unsubscribe users to/from notifications on
                this RFQ.
              </p>
            ) : (
              rfq.buyerNotificationUsers.map((user) => (
                <p key={user.id} className="text-sm text-gray-700">
                  {user.displayName}
                </p>
              ))
            )}
          </Card.Section>
        </Card>
      </Layout.Section>
    </Layout>
  )
}

export default Details
